// ArtifactViewerPage.jsx — Task 11. Renders one artifact: header (title, badge, period), the slot
// sequence via TemplateRenderer, a producing-agent footer, and a "Run again" action that seeds a
// pre-filled prompt into the Agent Studio chat.
//
// Data: GET /api/agent/companies/:slug/artifacts/:id → { id, title, template_key, period, created_at,
// agent_job_id, creator, payload }. A 404 renders the "Not found in this company" empty state (no leak).
//
// RUN AGAIN: writes sessionStorage['vesta-artifact-rerun'] = JSON{prompt} then navigates to
// /activity?mode=agent, where AgentStudioChat's mount effect reads+clears the key and sendText()s it.
import React, { useEffect, useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import TemplateRenderer from "../shell/artifacts/TemplateRenderer.jsx"
import { badgeFor } from "../shell/artifacts/templateViewConfig"
import { artifactModeFromSearch, artifactModePath } from "../shell/artifactModePath"

export const ARTIFACT_RERUN_KEY = "vesta-artifact-rerun"

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  } catch (_e) {
    return ""
  }
}

export default function ArtifactViewerPage({ companySlug }) {
  const { artifactId } = useParams()
  const navigate = useNavigate()
  const currentMode = artifactModeFromSearch(window.location.search)
  const [state, setState] = useState({ loading: true, notFound: false, artifact: null })

  useEffect(() => {
    let live = true
    if (!companySlug || !artifactId) {
      setState({ loading: false, notFound: true, artifact: null })
      return
    }
    fetch(`/api/agent/companies/${companySlug}/artifacts/${artifactId}`, { headers: { Accept: "application/json" } })
      .then((r) => {
        if (r.status === 404) return Promise.reject("not_found")
        return r.ok ? r.json() : Promise.reject(r.status)
      })
      .then((data) => {
        if (live) setState({ loading: false, notFound: false, artifact: data })
      })
      .catch((e) => {
        if (live) setState({ loading: false, notFound: e === "not_found", artifact: null })
      })
    return () => {
      live = false
    }
  }, [companySlug, artifactId])

  const runAgain = () => {
    const a = state.artifact
    if (!a) return
    const badgeLabel = badgeFor(a.template_key).label
    const prompt = `Run the ${badgeLabel} report again for the current period. Use the ${a.template_key} artifact template and fill every slot from live tool results only.`
    try {
      sessionStorage.setItem(ARTIFACT_RERUN_KEY, JSON.stringify({ prompt }))
    } catch (_e) {
      /* sessionStorage may be unavailable; navigation still lands the user in the chat */
    }
    navigate("/activity?mode=agent")
  }

  if (state.loading) return <div className="afq"><div className="afq-wrap"><div className="afq-note">Loading…</div></div></div>

  if (state.notFound) {
    return (
      <div className="afq" data-page="artifact-viewer">
        <div className="afq-wrap">
          <Link className="afq-back" to={artifactModePath("/artifacts", currentMode)}>← All AI documents</Link>
          <div className="afq-empty">
            <h3>Not found in this company</h3>
            <p>This AI document doesn’t exist here, or it belongs to another company.</p>
          </div>
        </div>
      </div>
    )
  }

  const a = state.artifact
  const b = badgeFor(a.template_key)
  const tint = `color-mix(in srgb, ${b.color} 12%, transparent)`
  return (
    <div className="afq" data-page="artifact-viewer">
      <div className="afq-vwrap">
        <Link className="afq-back" to={artifactModePath("/artifacts", currentMode)}>← All AI documents</Link>
        <div className="afq-vhead">
          <span className="afq-badge" style={{ color: b.color, background: tint }}>{b.label}</span>
          <span className="vs-admin-badge">Admin only</span>
          <h1 className="afq-vtitle">{a.title}</h1>
          {a.period ? <div className="afq-vmeta">{a.period}</div> : null}
        </div>

        <TemplateRenderer payload={a.payload} />

        <div className="afq-foot">
          <div className="afq-foot-credit">Generated by Vesta Agent · {fmtDate(a.created_at)}</div>
          <button className="afq-runagain" onClick={runAgain}>Run again</button>
        </div>
      </div>
    </div>
  )
}
